// ════════════════════════════════════════════════════════════════════════════
//  ManDana — Calendar Grid (the edit view)
//  ----------------------------------------------------------------------------
//  A direct-manipulation, time-blocked grid reached FROM the Today list via the
//  liquid morph (md-morph.js). One-hour rows starting at the hour of Fajr.
//
//    • Prayers are LOCKED anchors — soft full-width accent bands that events
//      flow around; they can't be dragged or resized.
//    • Events are draggable cards that snap to 5-minute steps, with elegant
//      top/bottom edge handles for resizing. Overlapping events split into
//      side-by-side columns (Apple/Google-calendar style).
//    • A live "now" hairline rides the true clock position (today only).
//    • Every morphable element is tagged [data-morph="<uid>"] so the engine can
//      fly it between its list row and its grid slot.
//
//  Exposes <GridView> on window for md-app.jsx.
// ════════════════════════════════════════════════════════════════════════════
const { Glass: GlassG, MDI: MDIG, mdFmt: mdFmtG, mdEnd: mdEndG, mdHM: mdHMG, mdRgba: rgbaG,
  mdIsLight: isLightG, mdVivid: vividG, mdDateForOffset: dateForG,
  ink: inkG, useMdLayout: useLayoutG, mdRecordTitle: titleG, mdRecordUid: uidG } = window;
const { useState: useStateG, useRef: useRefG, useEffect: useEffectG,
  useLayoutEffect: useLayoutEffectG, useMemo: useMemoG, useCallback: useCallbackG } = React;

/* ── grid geometry ── */
const G_START_FALLBACK = 282;   // used only when the prayer engine has no Fajr boundary
const G_PXH = 64;              // comfortable: 64 px / hour
const G_PPM = G_PXH / 60;       // px per minute
const G_GUTTER = 56;            // time-label gutter
const G_RPAD = 14;              // right padding of the track
const G_COLGAP = 5;             // gap between side-by-side event columns
const G_MINH = 38;              // floor height of a block, so its title stays readable
const G_MINDUR = 15;            // smallest event a resize can produce (min)
const G_NEWDUR = 30;            // default length of a tap-created block (min)
const SNAP = 5;                 // snap step (min)

const gSnap = (m) => Math.round(m / SNAP) * SNAP;

// life-class palette (mirrors md-today.jsx)
const G_EVENT_ICON = { e1: 'flag', e2: 'fork', e3: 'pen', e4: 'dumbbell', e5: 'chart', e6: 'compass', e7: 'gear' };
const G_EVENT_CLASS = {
  e1: { light: '#2563EB', dark: '#60A5FA' }, e2: { light: '#E11D48', dark: '#F43F5E' },
  e3: { light: '#F97316', dark: '#FB923C' }, e4: { light: '#20C997', dark: '#6EE7B7' },
  e5: { light: '#0F5132', dark: '#34D399' }, e6: { light: '#7C3AED', dark: '#A78BFA' },
  e7: { light: '#B08968', dark: '#D4C5B9' },
};

/* ── greedy interval-graph column packing (events only) ──
   returns { uid: { col, cols } } so overlapping events share a span as columns. */
function packColumns(events) {
  const evs = [...events].sort((a, b) => a.min - b.min || (b.min + b.dur) - (a.min + a.dur));
  const res = {};
  let cluster = [], clusterEnd = -1;
  const flush = () => {
    if (!cluster.length) return;
    const colEnds = [];
    cluster.forEach((ev) => {
      let placed = -1;
      for (let c = 0; c < colEnds.length; c++) { if (ev.min >= colEnds[c]) { placed = c; break; } }
      if (placed < 0) { placed = colEnds.length; colEnds.push(0); }
      colEnds[placed] = ev.min + ev.dur;
      res[ev.uid] = { col: placed };
    });
    cluster.forEach((ev) => { res[ev.uid].cols = colEnds.length; });
    cluster = []; clusterEnd = -1;
  };
  evs.forEach((ev) => {
    if (cluster.length && ev.min >= clusterEnd) flush();
    cluster.push(ev);
    clusterEnd = Math.max(clusterEnd, ev.min + ev.dur);
  });
  flush();
  return res;
}

/* ── block height ──
   Time first: a block is at least as tall as its own minutes. Below G_MINH it is
   floored so the title stays readable — but the floor is only honest in empty
   space, so it is capped by the room beneath the block (mdRoom, in md-overlap.js).
   A 15-minute record at 4:20 floored to 38px used to run 22px past its own end,
   straight through the Asr band starting at 4:35, and the grid showed a conflict
   the day did not have. Blocks that genuinely overlap start before this one ends,
   cap nothing, and are already sitting in their own column. */
function blockH(dur, roomMin) {
  const natural = (dur || 0) * G_PPM;
  const room = Number.isFinite(roomMin) ? roomMin * G_PPM : Infinity;
  return Math.max(natural, Math.min(G_MINH, room));
}

// shared transition for cards reflowing into new column geometry. The dragged
// card must follow the finger instantly (no left/top/width lag); idle cards glide.
const FLOW_T = 'left .26s cubic-bezier(.22,.61,.36,1), width .26s cubic-bezier(.22,.61,.36,1), top .26s cubic-bezier(.22,.61,.36,1), box-shadow .25s ease, transform .2s ease';
const DRAG_T = 'box-shadow .18s ease, transform .12s ease';

/* ── one draggable / resizable event card — no icon, name truncates, font + time
   adapt to height and column crowding. Title has priority: on a short card the
   start time is appended ONLY if it fits without truncating the title. ── */
function GridEvent({ ev, geom, accent, light, dragging, onGrab }) {
  const cls = G_EVENT_CLASS[ev.id];
  const col = cls ? vividG(light ? cls.light : cls.dark) : inkG(0.6);
  const h = geom.h;
  const tight = h < 52;             // no room for a separate time line
  /* A quarter-hour block hard against the next one is 16px tall — the padding
     alone would eat it, so type and padding both give way rather than the block
     growing past its own end. */
  const micro = h < 30;
  const crammed = (geom.cols || 1) >= 3;   // 3+ side-by-side → shrink type
  const nameSize = crammed ? 11.5 : micro ? 12 : 13.5;
  const metaSize = crammed ? 10 : micro ? 10 : 11.5;
  const timeStr = `${mdFmtG(minToHHMM(ev.min))} – ${mdEndG(ev.min, ev.dur)}`;
  const startStr = mdFmtG(minToHHMM(ev.min));
  const titleRef = useRefG(null);
  const timeRef = useRefG(null);

  // title-first: show the appended time only if the title isn't truncated by it
  useLayoutEffectG(() => {
    const t = titleRef.current, tm = timeRef.current;
    if (!tight || !t || !tm) { if (tm) tm.style.display = ''; return; }
    tm.style.display = '';
    void t.offsetWidth;
    if (t.scrollWidth > t.clientWidth + 1) tm.style.display = 'none';
  }, [geom.w, ev.name, ev.min, ev.dur, tight, crammed]);

  return (
    <div
      data-morph={ev.uid}
      onPointerDown={(e) => onGrab(e, ev, 'move')}
      style={{
        position: 'absolute', left: geom.x, top: geom.y, width: geom.w, height: h,
        zIndex: dragging ? 50 : 5,
        transition: dragging ? DRAG_T : FLOW_T,
        transform: dragging ? 'scale(1.025)' : 'none',
        touchAction: 'none', cursor: 'grab',
      }}>
      <GlassG radius={12} tint={rgbaG(col, light ? 0.16 : 0.2)} stroke={dragging ? 0.5 : 0.28}
        shadow glow={dragging}
        style={{
          height: '100%', padding: micro ? '0 8px' : crammed ? '5px 7px' : '6px 10px',
          display: 'flex', flexDirection: 'column', justifyContent: tight ? 'center' : 'flex-start', gap: 1,
          borderLeft: `3px solid ${rgbaG(col, 0.95)}`,
          boxShadow: dragging
            ? `0 14px 32px ${rgbaG(light ? '#1b263c' : '#000', light ? 0.22 : 0.6)}, 0 0 0 1px ${rgbaG(col, 0.4)}`
            : undefined,
        }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, minWidth: 0 }}>
          {/* a block drawn on the grid is a record before it is written, so it is
              titled the same way the day list titles one: "New Record" until the
              first line exists, then that line, live */}
          <span ref={titleRef} style={{ color: inkG(0.96), fontSize: nameSize, fontWeight: 600, lineHeight: 1.18,
            opacity: ev.name ? 1 : 0.6,
            flex: '0 1 auto', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{titleG(ev)}</span>
          {/* short cards: append start time only if it fits without truncating the title */}
          {tight &&
            <span ref={timeRef} style={{ color: inkG(0.5), fontSize: metaSize, fontWeight: 500, flexShrink: 0,
              fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}><bdi dir="ltr" style={{ unicodeBidi: 'isolate' }}>{startStr}</bdi></span>}
        </div>
        {!tight &&
          <div style={{ color: inkG(0.58), fontSize: metaSize, whiteSpace: 'nowrap',
            overflow: 'hidden', textOverflow: 'ellipsis', fontVariantNumeric: 'tabular-nums' }}>
            <bdi dir="ltr" style={{ unicodeBidi: 'isolate' }}>{timeStr}</bdi>
          </div>}
      </GlassG>

      {/* edge resize handles — slim pills inside the card edges, brighten on the active card */}
      <ResizeHandle pos="top" col={col} active={dragging} light={light} cardH={h} onGrab={(e) => onGrab(e, ev, 'top')} />
      <ResizeHandle pos="bottom" col={col} active={dragging} light={light} cardH={h} onGrab={(e) => onGrab(e, ev, 'bottom')} />
    </div>
  );
}

function ResizeHandle({ pos, col, active, light, cardH, onGrab }) {
  const top = pos === 'top';
  // 30% of the card, so the two handles always leave a middle to drag by —
  // on a 16px block a fixed 8px floor would have been the whole card.
  const hitH = Math.max(4, Math.min(13, (cardH || 40) * 0.3));
  return (
    <div
      onPointerDown={onGrab}
      style={{
        position: 'absolute', left: 8, right: 8, height: hitH, cursor: 'ns-resize',
        [top ? 'top' : 'bottom']: 0, display: 'grid', placeItems: 'center',
        touchAction: 'none', zIndex: 3,
      }}>
      <span style={{
        // a short block gets a slimmer grip, so the two edges read as edges
        // rather than as a stack of pills through the middle of the card
        width: active ? 28 : cardH < 30 ? 13 : 18, height: active ? 4 : cardH < 30 ? 2 : 3, borderRadius: 999,
        background: active ? rgbaG(col, 1) : inkG(light ? 0.2 : 0.3),
        boxShadow: active ? `0 0 9px ${rgbaG(col, 0.7)}` : 'none',
        transition: 'width .2s ease, height .2s ease, background .2s ease, box-shadow .2s ease',
      }} />
    </div>
  );
}

function minToHHMM(m) {
  const mm = ((m % 1440) + 1440) % 1440;
  return `${String(Math.floor(mm / 60)).padStart(2, '0')}:${String(mm % 60).padStart(2, '0')}`;
}

/* ── Done action — a sibling of the "+" FAB, but with a drawn check + swoosh ── */
function CheckFab({ accent, onClick }) {
  return (
    <button aria-label="Done" onClick={onClick} style={{
      position: 'absolute', insetInlineEnd: 22,
      /* 28px up from the foot of the panel is exactly where the home indicator
         is drawn. Where the panel around it has already floated clear, the
         token is zero there and this is the 28 it always was — paying twice
         would lift the button into the middle of nothing, which is the same
         defect wearing the other coat. */
      bottom: 'max(var(--md-safe-bottom), 28px)', zIndex: 70,
      width: 60, height: 60, borderRadius: '50%', border: 'none', cursor: 'pointer',
      display: 'grid', placeItems: 'center', background: accent,
      boxShadow: `0 10px 26px ${rgbaG(accent, 0.5)}, inset 0 1px 1px rgba(255,255,255,0.5)`,
      animation: 'mdFabPop .42s cubic-bezier(.22,.61,.36,1)', transition: 'transform .15s ease' }}
      onPointerDown={(e) => e.currentTarget.style.transform = 'scale(0.92)'}
      onPointerUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
      onPointerLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
      <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: `2px solid ${accent}`,
        animation: 'mdSwoosh .6s ease-out forwards', pointerEvents: 'none' }} />
      <svg aria-hidden="true" focusable="false" width="30" height="30" viewBox="0 0 24 24" fill="none">
        <path d="M5 12.5l4.3 4.3L19 7.2" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"
          style={{ strokeDasharray: 30, strokeDashoffset: 30, animation: 'mdCheckDraw .5s .14s cubic-bezier(.22,.61,.36,1) forwards' }} />
      </svg>
    </button>
  );
}

/* ── prayer card — a LOCKED column block. Joins the column packing so events
   overlapping a prayer push it into a side-by-side column, but its time and
   duration can't be changed (no drag, no resize handles). ── */
function PrayerCard({ p, geom, accent, light }) {
  const h = geom.h;
  const tight = h < 50;
  const crammed = (geom.cols || 1) >= 3;
  const nameSize = crammed ? 11.5 : 13;
  const nameRef = useRefG(null);
  const timeRef = useRefG(null);

  useLayoutEffectG(() => {
    const t = nameRef.current, tm = timeRef.current;
    if (!tight || !t || !tm) { if (tm) tm.style.display = ''; return; }
    tm.style.display = '';
    void t.offsetWidth;
    if (t.scrollWidth > t.clientWidth + 1) tm.style.display = 'none';
  }, [geom.w, p.name, p.nextDay, tight, crammed]);

  return (
    <div data-morph={p.uid} style={{
      position: 'absolute', left: geom.x, top: geom.y, width: geom.w, height: h, zIndex: 4,
      transition: FLOW_T, cursor: 'default' }}>
      <GlassG radius={12} tint={rgbaG(accent, light ? 0.15 : 0.2)} stroke={0.34} shadow
        style={{ height: '100%', padding: crammed ? '5px 6px' : '6px 9px', display: 'flex', flexDirection: 'column',
          justifyContent: tight ? 'center' : 'flex-start', gap: 1,
          borderLeft: `3px solid ${rgbaG(accent, 0.85)}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
          <span style={{ display: 'inline-flex', flexShrink: 0 }}>{MDIG.star('#F59E0B', crammed ? 10 : 12)}</span>
          <span ref={nameRef} style={{ color: inkG(0.95), fontSize: nameSize, fontWeight: 650, flex: '0 1 auto', minWidth: 0,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
          {p.nextDay &&
            <span style={{ flexShrink: 0, padding: '1px 6px', borderRadius: 999, fontSize: 9, fontWeight: 800,
              letterSpacing: 0.5, textTransform: 'uppercase', color: rgbaG(accent, 1),
              background: rgbaG(accent, light ? 0.16 : 0.26), border: `0.5px solid ${rgbaG(accent, 0.4)}` }}>Next Day</span>}
          {/* short prayer card: append start time only if the name still fits fully */}
          {tight &&
            <span ref={timeRef} style={{ color: inkG(0.5), fontSize: crammed ? 10 : 11, fontWeight: 500, flexShrink: 0,
              fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}><bdi dir="ltr" style={{ unicodeBidi: 'isolate' }}>{mdFmtG(p.t)}</bdi></span>}
          {!crammed && !p.nextDay && <span style={{ display: 'inline-flex', flexShrink: 0, opacity: 0.5, marginInlineStart: 'auto' }}>{lockGlyph(inkG(0.5))}</span>}
        </div>
        {!tight &&
          <div style={{ color: inkG(0.5), fontSize: crammed ? 10 : 11, fontWeight: 600, fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <bdi dir="ltr" style={{ unicodeBidi: 'isolate' }}>{mdFmtG(p.t)} – {mdEndG(p.min, geom.dur)}</bdi>
          </div>}
      </GlassG>
    </div>
  );
}

function lockGlyph(c) {
  return (
    <svg aria-hidden="true" focusable="false" width="11" height="11" viewBox="0 0 24 24" fill="none">
      <rect x="5" y="11" width="14" height="9" rx="2" stroke={c} strokeWidth="1.9" />
      <path d="M8 11V8a4 4 0 018 0v3" stroke={c} strokeWidth="1.9" strokeLinecap="round" />
    </svg>
  );
}

/* ── the grid edit screen ──
   The committed draft isn't returned via a prop callback — instead GridView hands
   App a getter through registerExit(() => draft), which App reads on close. */
function GridView({ accent, eventsInit, prayers, nowSec, isToday, dayOffset, gridStart, gridEnd,
  focalMin, onClose, onSwitchDay, registerExit, morph, phase,
  onComposeRecord, onScheduleRecord, onTapBlock, registerApi, unscheduled = [] }) {
  const light = isLightG();
  const L = useLayoutG ? useLayoutG() : { wide: false, phone: true };
  /* Escape means Done — the same door as the check FAB. The grid was the one
     surface in the app the keyboard could not leave; Done commits (App reads
     the draft through registerExit on close), so nothing is lost. */
  window.useMdDismissLayer(onClose, true, null, 'grid');
  const [draft, setDraft] = useStateG(() => eventsInit.map((e) => ({ ...e })));
  const [dragUid, setDragUid] = useStateG(null);
  const [tip, setTip] = useStateG(null);       // live { x, y, text } while dragging
  const [ghost, setGhost] = useStateG(null);   // provisional new block being drawn / dropped
  const [pillDragUid, setPillDragUid] = useStateG(null); // tray pill currently being dragged in
  const scrollRef = useRefG(null);
  const scaffoldRef = useRefG(null);
  const headerRef = useRefG(null);
  const contentRef = useRefG(null);
  const gestureRef = useRefG(null);
  const createRef = useRefG(null);   // active draw-to-create gesture
  const trayRef = useRefG(null);     // active tray-pill drag-to-schedule gesture
  const draftRef = useRefG(draft);   // latest draft, for the (deps-free) pointer listeners
  draftRef.current = draft;
  const tapRef = useRefG(onTapBlock);
  tapRef.current = onTapBlock;
  const date = dateForG(dayOffset);
  const gStart = Number.isFinite(gridStart) ? gridStart : G_START_FALLBACK;
  const gEnd = Number.isFinite(gridEnd) && gridEnd > gStart ? gridEnd : gStart + 1440;
  const gTotal = (gEnd - gStart) * G_PPM;
  const gToY = (min) => (min - gStart) * G_PPM;

  // column geometry — prayers join the packing (locked) so events overlapping a
  // prayer push it into a side-by-side column. The window rule is md-theme's.
  const trackLeft = G_GUTTER;
  const prayersG = useMemoG(() => {
    return (prayers || []).map((p) => ({ ...p, dur: window.mdPrayerWindowEnd(p.min) - p.min }));
  }, [prayers]);
  const cols = useMemoG(() => packColumns([...prayersG, ...draft]), [prayersG, draft]);
  const room = useMemoG(() => window.mdRoom([...prayersG, ...draft]), [prayersG, draft]);
  // the ghost is not a block yet, so it reads the room under itself separately
  const ghostRoom = useMemoG(() => (ghost
    ? window.mdRoom([...prayersG, ...draft, { uid: '__mdGhost', min: ghost.min, dur: ghost.dur }]).__mdGhost
    : Infinity), [ghost, prayersG, draft]);

  const trackW = useCallbackG((containerW) => containerW - trackLeft - G_RPAD, []);
  const [cw, setCw] = useStateG(393);
  useLayoutEffectG(() => {
    const el = scrollRef.current; if (!el) return;
    const set = () => setCw(el.clientWidth);
    set();
    const ro = new ResizeObserver(set); ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const geomFor = (ev) => {
    const c = cols[ev.uid] || { col: 0, cols: 1 };
    const tw = trackW(cw);
    const colW = tw / c.cols;
    return {
      x: trackLeft + c.col * colW,
      w: colW - G_COLGAP,
      y: gToY(ev.min),
      h: blockH(ev.dur, room[ev.uid]),
      dur: ev.dur,
      cols: c.cols,
    };
  };

  // initial scroll to the focal block, then play the liquid enter morph
  useLayoutEffectG(() => {
    const el = scrollRef.current; if (!el) return;
    const target = gToY(focalMin != null ? focalMin : (isToday ? nowSec / 60 : gStart));
    el.scrollTop = Math.max(0, target - el.clientHeight * 0.34);

    if (!morph || !morph.origin) return;
    // focal point in the scroll content's local coords (for the radial scaffold reveal)
    const fx = G_GUTTER + (trackW(cw)) / 2;
    const fy = gToY(focalMin != null ? focalMin : gStart);
    const sf = scaffoldRef.current;
    if (sf) {
      sf.style.transformOrigin = `${fx}px ${fy}px`;
      sf.style.opacity = '0'; sf.style.filter = 'blur(8px)'; sf.style.transform = 'scale(1.05)';
    }
    const hd = headerRef.current;
    if (hd) { hd.style.opacity = '0'; hd.style.transform = 'translateY(-10px)'; }
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (sf) {
        sf.style.transition = 'opacity .52s ease, filter .52s ease, transform .56s cubic-bezier(.22,.61,.36,1)';
        sf.style.opacity = '1'; sf.style.filter = 'none'; sf.style.transform = 'none';
      }
      if (hd) {
        hd.style.transition = 'opacity .44s ease, transform .5s cubic-bezier(.22,.61,.36,1)';
        hd.style.opacity = '1'; hd.style.transform = 'none';
      }
    }));
    // FLIP the event blocks + prayer bands from their list rows into their grid slots
    if (contentRef.current && window.mdMorphIn) {
      window.mdMorphIn(contentRef.current, morph.origin, { focal: morph.focal, stagger: 150, duration: 640 });
    }
  }, []);

  // exit visuals — scaffold + header recede (App flies the blocks back out)
  useEffectG(() => {
    if (phase !== 'exit') return;
    const sf = scaffoldRef.current, hd = headerRef.current;
    const fx = G_GUTTER + (trackW(cw)) / 2;
    const fy = gToY(focalMin != null ? focalMin : gStart);
    if (sf) {
      sf.style.transformOrigin = `${fx}px ${fy}px`;
      sf.style.transition = 'opacity .42s ease, filter .42s ease, transform .46s cubic-bezier(.4,0,.2,1)';
      sf.style.opacity = '0'; sf.style.filter = 'blur(7px)'; sf.style.transform = 'scale(1.05)';
    }
    if (hd) {
      hd.style.transition = 'opacity .36s ease, transform .42s ease';
      hd.style.opacity = '0'; hd.style.transform = 'translateY(-8px)';
    }
  }, [phase]);

  /* ── unified pointer gesture: move / top-resize / bottom-resize ── */
  const onGrab = (e, ev, mode) => {
    e.stopPropagation();
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) {}
    gestureRef.current = {
      mode, uid: ev.uid, pid: e.pointerId,
      startY: e.clientY, startMin: ev.min, startDur: ev.dur,
      end: ev.min + ev.dur, moved: false, el: e.currentTarget,
    };
    setDragUid(ev.uid);
  };

  useEffectG(() => {
    const onMove = (e) => {
      const g = gestureRef.current; if (!g || e.pointerId !== g.pid) return;
      const dyMin = (e.clientY - g.startY) / G_PPM;
      if (Math.abs(e.clientY - g.startY) > 3) g.moved = true;
      setDraft((prev) => prev.map((it) => {
        if (it.uid !== g.uid) return it;
        if (g.mode === 'move') {
          let ns = gSnap(g.startMin + dyMin);
          ns = Math.max(gStart, Math.min(gEnd - g.startDur, ns));
          return { ...it, min: ns };
        }
        if (g.mode === 'top') {
          let ns = gSnap(g.startMin + dyMin);
          ns = Math.max(gStart, Math.min(g.end - G_MINDUR, ns));
          return { ...it, min: ns, dur: g.end - ns };
        }
        // bottom
        let ne = gSnap(g.end + dyMin);
        ne = Math.max(g.startMin + G_MINDUR, Math.min(gEnd, ne));
        return { ...it, dur: ne - g.startMin };
      }));
    };
    const onUp = (e) => {
      const g = gestureRef.current; if (!g || e.pointerId !== g.pid) return;
      gestureRef.current = null;
      setDragUid(null);
      setTip(null);
      // a tap (no drag) on a Record block's body re-opens it in the compose sheet
      if (!g.moved && g.mode === 'move') {
        const ev = (draftRef.current || []).find((d) => d.uid === g.uid);
        if (ev && ev.record && tapRef.current) tapRef.current(ev);
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, []);

  // live tooltip reflecting the dragged block's snapped window
  useEffectG(() => {
    if (!dragUid) { setTip(null); return; }
    const ev = draft.find((d) => d.uid === dragUid); if (!ev) return;
    const g = geomFor(ev);
    const below = g.y < 46;   // near the top: float the marker below the block instead
    setTip({
      y: below ? g.y + g.h : g.y,         // content coordinates (scrolls with the block)
      cx: g.x + g.w / 2,
      below,
      text: `${mdFmtG(minToHHMM(ev.min))} – ${mdEndG(ev.min, ev.dur)}`,
      sub: mdHMG(ev.dur),
    });
  }, [draft, dragUid]);

  // expose the committed draft to the exit morph. Sync each block's display `t`
  // from its (possibly-edited) `min` so the Today timeline reads the right start.
  useEffectG(() => { registerExit && registerExit(() => draft.map((d) => ({ ...d, t: minToHHMM(d.min) }))); }, [draft, registerExit]);

  // ── draft mutation API — lets App write a new/edited Record block into the grid
  //    in place (no remount) once the compose sheet resolves over the grid. ──
  const upsertDraft = useCallbackG((r, sourceOffset = dayOffset) => setDraft((prev) => {
    const placed = { ...r, min: r.min + (sourceOffset - dayOffset) * 1440 };
    const i = prev.findIndex((x) => x.uid === r.uid);
    if (i >= 0) { const n = prev.slice(); n[i] = { ...n[i], ...placed }; return n; }
    return [...prev, placed];
  }), [dayOffset]);
  const removeDraft = useCallbackG((uid) => setDraft((prev) => prev.filter((x) => x.uid !== uid)), []);
  const acceptsOffset = useCallbackG((offset) => offset === dayOffset || offset === dayOffset + 1, [dayOffset]);
  useEffectG(() => { registerApi && registerApi({ upsertDraft, removeDraft, acceptsOffset }); }, [registerApi, upsertDraft, removeDraft, acceptsOffset]);

  // clientY → snapped minute-of-day, in grid coordinates (accounts for scroll)
  const yToMin = (clientY) => {
    const c = contentRef.current; if (!c) return gStart;
    const y = clientY - c.getBoundingClientRect().top;
    return gSnap(gStart + y / G_PPM);
  };

  // ── draw-to-create: tap empty grid drops a 30-min block; drag draws it to size.
  //    On release, hand the provisional block up so App opens the compose sheet. ──
  const fireCreate = (min, dur) => {
    const uid = uidG();
    const rec = { uid, kind: 'event', record: true, allDay: false, domain: null, name: '', body: '', min, dur, t: minToHHMM(min) };
    upsertDraft(rec);                       // show the block immediately, beneath the rising sheet
    onComposeRecord && onComposeRecord(rec);
  };
  // Quick tap on empty space drops a default block; a press-and-HOLD arms the
  // draw-to-size mode (so a plain vertical drag still scrolls the grid).
  const showCreateTip = (min, dur) => {
    const y = gToY(min);
    setTip({ y, cx: trackLeft + trackW(cw) / 2, below: y < 46, text: `${mdFmtG(minToHHMM(min))} – ${mdEndG(min, dur)}`, sub: mdHMG(dur) });
  };
  const onCreateDown = (e) => {
    if (e.button != null && e.button !== 0) return;
    const startMin = yToMin(e.clientY);
    const g = { pid: e.pointerId, startMin, startY: e.clientY, moved: false, armed: false, cur: { min: startMin, dur: G_NEWDUR }, el: e.currentTarget };
    createRef.current = g;
    g.timer = setTimeout(() => {
      const gg = createRef.current; if (!gg || gg.pid !== e.pointerId) return;
      gg.armed = true;
      try { gg.el.setPointerCapture(gg.pid); } catch (err) {}
      if (navigator.vibrate) try { navigator.vibrate(9); } catch (err) {}
      setGhost({ min: gg.startMin, dur: G_NEWDUR });
      showCreateTip(gg.startMin, G_NEWDUR);
    }, 280);
  };
  const onCreateMove = (e) => {
    const g = createRef.current; if (!g || e.pointerId !== g.pid) return;
    const dist = Math.abs(e.clientY - g.startY);
    if (!g.armed) { if (dist > 10) { clearTimeout(g.timer); createRef.current = null; } return; } // moved before hold → let it scroll
    if (dist > 4) g.moved = true;
    const dyMin = (e.clientY - g.startY) / G_PPM;
    const a = g.startMin, b = gSnap(g.startMin + dyMin);
    let min = Math.min(a, b), end = Math.max(a, b);
    if (end - min < G_MINDUR) end = min + G_MINDUR;
    min = Math.max(gStart, min); end = Math.min(gEnd, end);
    g.cur = { min, dur: end - min };
    setGhost(g.cur);
    showCreateTip(min, end - min);
  };
  const onCreateUp = (e) => {
    const g = createRef.current; if (!g || e.pointerId !== g.pid) return;
    clearTimeout(g.timer);
    createRef.current = null;
    const armed = g.armed;
    setGhost(null); setTip(null);
    if (armed) {
      let { min, dur } = g.cur;
      min = Math.max(gStart, Math.min(gEnd - dur, gSnap(min)));
      fireCreate(min, dur);
    } else if (!g.moved) {                       // quick tap → default-length block
      const min = Math.max(gStart, Math.min(gEnd - G_NEWDUR, g.startMin));
      fireCreate(min, G_NEWDUR);
    }
  };
  const onCreateCancel = () => { const g = createRef.current; if (g) clearTimeout(g.timer); createRef.current = null; setGhost(null); setTip(null); };

  // ── drag an Unscheduled record onto the grid to give it a time ──
  const scheduleRec = (rec, min, dur) => {
    const r = { ...rec, kind: 'event', record: true, allDay: false, id: rec.domain || undefined, min, dur, t: minToHHMM(min) };
    upsertDraft(r);
    onScheduleRecord && onScheduleRecord(rec.uid);
  };
  const onPillDown = (e, rec) => {
    e.stopPropagation();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) {}
    trayRef.current = { pid: e.pointerId, rec, startX: e.clientX, startY: e.clientY, moved: false, over: false, cur: null };
    setPillDragUid(rec.uid);
  };
  const onPillMove = (e) => {
    const g = trayRef.current; if (!g || e.pointerId !== g.pid) return;
    if (Math.hypot(e.clientX - g.startX, e.clientY - g.startY) > 6) g.moved = true;
    if (!g.moved) return;
    const sc = scrollRef.current; const r = sc ? sc.getBoundingClientRect() : null;
    const over = r ? (e.clientY > r.top + 8 && e.clientY < r.bottom) : false;
    g.over = over;
    if (over) {
      const min = Math.max(gStart, Math.min(gEnd - G_NEWDUR, yToMin(e.clientY)));
      g.cur = { min, dur: G_NEWDUR };
      setGhost(g.cur);
      const y = gToY(min);
      setTip({ y, cx: trackLeft + trackW(cw) / 2, below: y < 46, text: `${mdFmtG(minToHHMM(min))} – ${mdEndG(min, G_NEWDUR)}`, sub: titleG(g.rec) });
    } else { g.cur = null; setGhost(null); setTip(null); }
  };
  const onPillUp = (e) => {
    const g = trayRef.current; if (!g || e.pointerId !== g.pid) return;
    trayRef.current = null;
    setPillDragUid(null); setGhost(null); setTip(null);
    if (g.moved && g.over && g.cur) scheduleRec(g.rec, g.cur.min, g.cur.dur);
  };

  const nowMin = isToday ? nowSec / 60 : null;
  const yNow = nowMin != null && nowMin >= gStart && nowMin <= gEnd ? gToY(nowMin) : null;
  const nowLabel = nowMin != null ? `${Math.floor(nowMin / 60) % 12 || 12}:${String(Math.floor(nowMin % 60)).padStart(2, '0')}` : '';
  const nowAp = nowMin != null && Math.floor(nowMin / 60) % 24 >= 12 ? 'PM' : 'AM';

  const hours = [];
  // clean hour lines on every whole hour through the track — the Fajr card sits at the
  // very top (y=0) on its own, so no separate marker line is drawn above it
  for (let m = Math.ceil((gStart + 1) / 60) * 60; m <= gEnd; m += 60) {
    const H = Math.floor(m / 60) % 24;
    hours.push({ y: gToY(m), label: `${H % 12 || 12} ${H >= 12 ? 'PM' : 'AM'}` });
  }
  // half-hour ticks on every whole :30 within range (skip ones that coincide with a line)
  const halfTicks = [];
  for (let m = Math.ceil(gStart / 30) * 30; m < gEnd; m += 30) {
    if (m % 60 === 0) continue;
    halfTicks.push(gToY(m));
  }

  /* WHERE THE TOP OF THIS SCREEN IS. The grid opens INSIDE the day panel, and
     asks unconditionally: where the panel floats it has already paid and zeroes
     the token for everything within, so this resolves to the rest below.
     The expression used to ask `phone` before reading the environment at all.
     That got the phone right and lost an iPad in landscape, where the panel is
     docked, flush to the top of the screen, and the status bar is still exactly
     where it was. */
  const gridTopPad = L.phone ? 14 : 18;
  const headPad = `max(var(--md-safe-top), ${gridTopPad}px)`;

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
      fontFamily: 'var(--md-body)' }}>
      {/* ── edit header: the live week bar (continuity with Today) + an "editing" cue ── */}
      <div ref={headerRef} style={{ flexShrink: 0, padding: `${headPad} 16px 8px`, zIndex: 20, position: 'relative',
        backdropFilter: 'blur(18px) saturate(150%)', WebkitBackdropFilter: 'blur(18px) saturate(150%)',
        background: light
          ? 'linear-gradient(180deg, rgba(247,248,249,0.96), rgba(241,243,245,0.86))'
          : 'linear-gradient(180deg, rgba(12,12,14,0.82), rgba(12,12,14,0.62))',
        borderBottom: `0.75px solid ${inkG(0.08)}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 2 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: accent,
            boxShadow: `0 0 7px ${rgbaG(accent, 0.75)}` }} />
          <span className="md-label" style={{ color: rgbaG(accent, 0.95), fontSize: 11, fontWeight: 400 }}>
            Editing
          </span>
        </div>
        {window.WeekBar &&
          React.createElement(window.WeekBar, {
            accent, selectedOffset: dayOffset,
            onSelectDay: (off) => onSwitchDay && onSwitchDay(off),
            onSwipeWeek: (dir) => { const wi = Math.floor((dayOffset + 6) / 7) + dir; onSwitchDay && onSwitchDay(dir > 0 ? wi * 7 - 6 : wi * 7); },
          })}
      </div>

      {/* ── Unscheduled tray — the day's untimed Records, draggable onto the grid ── */}
      {unscheduled && unscheduled.length > 0 &&
        <div style={{ flexShrink: 0, position: 'relative', zIndex: 15, padding: '9px 16px 10px',
          borderBottom: `0.75px solid ${inkG(0.06)}`,
          background: light ? 'rgba(241,243,245,0.68)' : 'rgba(12,12,14,0.4)' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, padding: '0 1px 7px' }}>
            <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1.3, textTransform: 'uppercase', color: inkG(0.5) }}>Unscheduled</span>
            <span style={{ fontSize: 10.5, fontWeight: 500, color: inkG(0.34) }}>drag onto the grid to give it a time</span>
          </div>
          <div className="md-scroll" style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 2 }}>
            {unscheduled.map((rec) => {
              const col = rec.domain && window.mdDomainColor ? window.mdDomainColor(rec.domain, light) : inkG(0.42);
              const dragging = pillDragUid === rec.uid;
              return (
                <div key={rec.uid} onPointerDown={(e) => onPillDown(e, rec)} onPointerMove={onPillMove}
                  onPointerUp={onPillUp} onPointerCancel={onPillUp}
                  style={{ touchAction: 'none', cursor: 'grab', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '7px 13px 7px 9px', borderRadius: 999, maxWidth: 220,
                    background: light ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.06)',
                    border: `0.75px solid ${rec.domain ? rgbaG(col, 0.4) : inkG(0.12)}`,
                    boxShadow: dragging ? `0 8px 22px ${rgbaG(light ? '#1b263c' : '#000', light ? 0.18 : 0.5)}` : (light ? '0 1px 2px rgba(40,60,110,0.05)' : 'none'),
                    opacity: dragging ? 0.45 : 1, transform: dragging ? 'scale(0.96)' : 'none',
                    transition: 'opacity .16s ease, transform .16s ease, box-shadow .16s ease' }}>
                  <span style={{ width: 22, height: 22, borderRadius: 7, flexShrink: 0, display: 'grid', placeItems: 'center',
                    background: rec.domain ? rgbaG(col, light ? 0.16 : 0.22) : inkG(0.06) }}>
                    {rec.domain && window.mdDomainIcon ? window.mdDomainIcon(rec.domain, col, 13)
                      : <span style={{ width: 7, height: 7, borderRadius: '50%', border: `1.6px solid ${inkG(0.35)}` }} />}
                  </span>
                  <span style={{ color: inkG(0.9), fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{titleG(rec)}</span>
                </div>);
            })}
          </div>
        </div>}

      {/* ── scrollable grid ── */}
      <div ref={scrollRef} className="md-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', position: 'relative' }}>
        <div ref={contentRef} style={{ position: 'relative', height: gTotal + 112, width: '100%' }}>
          {/* hour lines + gutter labels (scaffold — revealed radially on enter) */}
          <div ref={scaffoldRef} style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
          {hours.map((h, i) => (
            <div key={i} style={{ position: 'absolute', left: 0, right: 0, top: h.y, height: 0 }}>
              <span style={{ position: 'absolute', left: 0, top: -7, width: G_GUTTER - 12, textAlign: 'right',
                color: inkG(0.4), fontSize: 10.5,
                fontWeight: 600, fontVariantNumeric: 'tabular-nums', letterSpacing: 0.2 }}>
                <bdi dir="ltr" className="md-display" style={{ unicodeBidi: 'isolate' }}>{h.label}</bdi>
              </span>
              <span style={{ position: 'absolute', left: G_GUTTER - 4, right: 6, top: 0, height: 1,
                background: inkG(0.07) }} />
            </div>
          ))}
          {/* half-hour ticks */}
          {halfTicks.map((y, i) => (
            <span key={'hh' + i} style={{ position: 'absolute', left: G_GUTTER - 4, right: 6, top: y, height: 1,
              background: inkG(0.035) }} />
          ))}

          </div>{/* /scaffold */}

          {/* create surface — sits above the scaffold but BELOW every card (z 1),
              so a press only starts a new block on genuinely empty grid space */}
          <div onPointerDown={onCreateDown} onPointerMove={onCreateMove} onPointerUp={onCreateUp} onPointerCancel={onCreateCancel}
            style={{ position: 'absolute', left: trackLeft, top: 0, right: G_RPAD, bottom: 0, zIndex: 1, touchAction: 'pan-y' }} />

          {/* prayer cards — locked columns; events overlapping them split side-by-side */}
          {prayersG.map((p) => <PrayerCard key={p.uid} p={p} geom={geomFor(p)} accent={accent} light={light} />)}

          {/* event blocks */}
          {draft.map((ev) => (
            <GridEvent key={ev.uid} ev={ev} geom={geomFor(ev)} accent={accent} light={light}
              dragging={dragUid === ev.uid} onGrab={onGrab} />
          ))}

          {/* provisional “new block” ghost — drawn while creating or dropping a record */}
          {ghost &&
            <div style={{ position: 'absolute', left: trackLeft, width: Math.max(0, trackW(cw) - G_COLGAP),
              top: gToY(ghost.min), height: blockH(ghost.dur, ghostRoom), zIndex: 8, pointerEvents: 'none',
              borderRadius: 12, border: `1.5px dashed ${rgbaG(accent, 0.8)}`, background: rgbaG(accent, light ? 0.13 : 0.2),
              display: 'grid', placeItems: 'center', boxShadow: `0 8px 24px ${rgbaG(accent, light ? 0.22 : 0.4)}` }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: rgbaG(accent, 1), fontSize: 12.5, fontWeight: 750 }}>
                <svg aria-hidden="true" focusable="false" width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke={rgbaG(accent, 1)} strokeWidth="2.4" strokeLinecap="round" /></svg>
                New
              </span>
            </div>}

          {/* now line */}
          {yNow != null &&
            <div style={{ position: 'absolute', left: G_GUTTER - 4, right: 6, top: yNow, height: 0, zIndex: 9, pointerEvents: 'none' }}>
              <span style={{ position: 'absolute', left: -6, top: -4.5, width: 9, height: 9, borderRadius: '50%',
                background: accent, boxShadow: `0 0 0 3px ${rgbaG(accent, 0.18)}, 0 1px 5px ${rgbaG(accent, 0.6)}` }} />
              <span style={{ position: 'absolute', left: 6, right: 44, top: 0, height: 1.5, borderRadius: 2,
                background: `linear-gradient(90deg, ${rgbaG(accent, 0.9)}, ${rgbaG(accent, 0.5)})` }} />
              <span style={{ position: 'absolute', right: 0, top: -8, display: 'inline-flex', alignItems: 'baseline', gap: 2,
                background: accent, color: '#fff', fontSize: 10, fontWeight: 800, padding: '2px 6px', borderRadius: 999,
                fontVariantNumeric: 'tabular-nums', boxShadow: `0 2px 9px ${rgbaG(accent, 0.5)}` }}>
                <bdi dir="ltr" style={{ unicodeBidi: 'isolate' }}>{nowLabel}<span style={{ fontSize: 7, opacity: 0.85 }}>{nowAp}</span></bdi>
              </span>
            </div>}
        </div>

        {/* live time · duration marker — follows the dragged/resized block */}
        {tip &&
          <div style={{ position: 'absolute', left: Math.max(G_GUTTER + 48, Math.min(cw - 52, tip.cx)), top: tip.y,
            zIndex: 60, pointerEvents: 'none',
            transform: `translate(-50%, ${tip.below ? '9px' : '-118%'})`, transition: 'top .08s linear' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '5px 8px 5px 10px', borderRadius: 999,
              background: light ? 'rgba(255,255,255,0.94)' : 'rgba(26,28,36,0.92)',
              backdropFilter: 'blur(12px) saturate(140%)', WebkitBackdropFilter: 'blur(12px) saturate(140%)',
              border: `0.75px solid ${inkG(0.12)}`,
              boxShadow: light ? '0 6px 18px rgba(40,60,110,0.18)' : '0 6px 20px rgba(0,0,0,0.5)',
              fontSize: 12, fontWeight: 700, color: inkG(0.92), fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
              <span>{tip.text}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', padding: '1px 7px', borderRadius: 999,
                background: rgbaG(accent, light ? 0.16 : 0.26), color: light ? rgbaG(accent, 1) : '#fff',
                fontSize: 11, fontWeight: 800 }}>{tip.sub}</span>
            </div>
          </div>}
      </div>

      {/* Done — check FAB sibling of the "+" button */}
      <CheckFab accent={accent} onClick={onClose} />
    </div>
  );
}

/* minToHHMM is here for md-app.jsx and md-sync.js, not for the grid's benefit.
   It is a pure time formatter living in a VIEW module, and md-sync.js reaching
   into it to repair a record is a coupling that only holds because index.html
   loads this file before anything can trigger a sync. It belongs with the data
   layer. The grid's own geometry — its Fajr bounds,
   G_PXH, packColumns — is private and stays that way. */
Object.assign(window, { GridView, minToHHMM });
